import { ConversationDetails } from "@/types/conversation/conversation";
import { buildRecurringSignals } from "../utils/aggregates";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface InsightsRecurringSignalsProps {
  conversations: ConversationDetails[];
  onAskAbout: (signal: string) => void;
}

export const InsightsRecurringSignals = ({
  conversations,
  onAskAbout,
}: InsightsRecurringSignalsProps) => {
  const signals = buildRecurringSignals(conversations);
  const hasRecurring = signals.some((s) => s.count >= 2);

  const title = hasRecurring ? "Recurring signals" : "Insights & recommendations";
  const subtitle = hasRecurring
    ? "Patterns showing up across multiple calls"
    : "What your Genies flagged in recent calls";

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <GenieIcon
          name="lightbulb"
          size={24}
          color="#4E9CFF"
          secondaryColor="#FF4DB8"
          gradientEnabled
          label={title}
        />
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
      </div>

      <div className="p-5">
        {signals.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">
              Once a few conversations are analyzed, insights and recommendations will surface here.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {signals.map((s, idx) => {
              const isRecurring = s.count >= 2;
              const kindLabel = s.kind === "insight" ? "Insight" : "Recommendation";
              return (
                <li key={idx}>
                  <button
                    type="button"
                    onClick={() => onAskAbout(s.text)}
                    className="w-full text-left rounded-xl border border-gray-100 hover:border-[#4E9CFF]/40 hover:bg-[#4E9CFF]/[0.02] transition-all p-3 group"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className={`flex-shrink-0 inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-lg text-[10px] font-bold ${
                          s.kind === "insight"
                            ? "bg-[#FFBB33]/15 text-[#B8860B]"
                            : "bg-[#4E9CFF]/10 text-[#4E9CFF]"
                        }`}
                        title={isRecurring ? `${s.count} calls` : kindLabel}
                      >
                        {isRecurring ? `×${s.count}` : kindLabel.charAt(0)}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1 flex items-center gap-2">
                          <span>{kindLabel}</span>
                          {isRecurring && (
                            <span className="text-[#4E9CFF] normal-case tracking-normal">
                              · {s.count} calls
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-800 leading-relaxed line-clamp-2 group-hover:text-gray-900">
                          {s.text}
                        </p>
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};
